import { useEffect, useRef, type PointerEvent, type SyntheticEvent } from "react"

/*
 * A long-press on touch, as a phone's Board opens a card's Move to… with one: a finger held still
 * for half a second. A finger that moves, as one scrolling the page does, or lifts sooner is an
 * ordinary touch, and the browser keeps it. A mouse never long-presses.
 */

/** How long a finger is held before the press is a long one, in ms. */
const holdFor = 500

/** How far a held finger may wander before it is scrolling instead, in px. */
const wander = 8

/**
 * Handlers to spread on the element that is long-pressed, calling `onLongPress` once the finger has
 * been held. The touch that long-pressed ends there: it opens no link and selects no text, and the
 * browser's own menu for a long-press (Android's) stays shut. `enabled: false` gives none.
 */
export function useLongPress(onLongPress: () => void, { enabled = true } = {}) {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const from = useRef<{ x: number; y: number }>(undefined)
  const pressed = useRef(false)
  const touching = useRef(false)
  const latest = useRef(onLongPress)
  useEffect(() => {
    latest.current = onLongPress
  })
  useEffect(() => () => clearTimeout(timer.current), [])
  if (!enabled) return {}

  const stop = () => {
    clearTimeout(timer.current)
    from.current = undefined
  }
  const swallow = (event: SyntheticEvent) => {
    if (!pressed.current) return
    event.preventDefault()
    event.stopPropagation()
  }
  const settle = () => {
    pressed.current = false
  }
  return {
    onPointerDown: (event: PointerEvent) => {
      touching.current = event.pointerType !== "mouse"
      pressed.current = false
      if (!touching.current || !event.isPrimary) return
      from.current = { x: event.clientX, y: event.clientY }
      timer.current = setTimeout(() => {
        pressed.current = true
        from.current = undefined
        if ("vibrate" in navigator) navigator.vibrate(10)
        latest.current()
      }, holdFor)
    },
    onPointerMove: (event: PointerEvent) => {
      const start = from.current
      if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > wander) stop()
    },
    onPointerUp: stop,
    onPointerCancel: stop,
    // Android opens its own menu for a long-pressed link; the press is ours.
    onContextMenu: (event: SyntheticEvent) => {
      if (touching.current) event.preventDefault()
    },
    // Lifting the finger after a long-press makes no click, so nothing under it is pressed: not
    // the link, nor the sheet the long-press just opened over it.
    onTouchEnd: (event: SyntheticEvent) => {
      swallow(event)
      // A browser that clicks anyway does so at once; a click well after is the keyboard's.
      if (pressed.current) setTimeout(settle, 600)
    },
    onClickCapture: (event: SyntheticEvent) => {
      swallow(event)
      settle()
    },
  }
}
